import { useState, useEffect } from 'react';
import { 
  collection, 
  getDocs, 
  doc,
  getDoc,
  query, 
  orderBy,
  where
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { useToast } from '../components/ui/Toast';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { usePermissions } from '../hooks/usePermissions';
import { useAuth } from '../hooks/useAuth';
import { logAdminActivity } from '../services/adminActivityLog';
import Card, { CardHeader, CardBody } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import { Input } from '../components/ui/FormField';
import AdminNotes from '../components/AdminNotes';
import { 
  Search, 
  Package,
  MapPin,
  User,
  Phone,
  Calendar,
  Clock,
  DollarSign,
  Navigation,
  FileText,
  X,
  Eye,
  Filter,
  Download,
  CheckCircle,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Mail,
  Truck,
  CreditCard,
  MessageSquare,
  StickyNote,
  Image as ImageIcon
} from 'lucide-react';

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [exactDate, setExactDate] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [customerInfo, setCustomerInfo] = useState(null);
  const [loadingCustomer, setLoadingCustomer] = useState(false);
  const [driverInfo, setDriverInfo] = useState(null);
  const [loadingDriver, setLoadingDriver] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [showImageModal, setShowImageModal] = useState(false);
  const [cancelOrder, setCancelOrder] = useState(null);
  const [cancelRefundDriver, setCancelRefundDriver] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelSubmitting, setCancelSubmitting] = useState(false);
  const { hasPermission } = usePermissions();
  const { user: currentAdmin } = useAuth();
  const canCancel = hasPermission('cancel_orders');
  const [selectedImage, setSelectedImage] = useState(null);
  const ordersPerPage = 20;
  const { toast } = useToast();

  useEffect(() => {
    fetchOrders();
  }, []);

  useEffect(() => {
    filterOrders();
  }, [orders, searchTerm, statusFilter, dateFilter, exactDate]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, dateFilter, exactDate]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const ordersRef = collection(db, 'orders');
      const q = query(ordersRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      
      const ordersData = await Promise.all(
        snapshot.docs.map(async (orderDoc) => {
          const orderData = orderDoc.data();
          
          let driverName = 'Not assigned';
          if (orderData.driverId) {
            try {
              const driverDoc = await getDoc(doc(db, 'users', orderData.driverId));
              if (driverDoc.exists()) {
                const driverData = driverDoc.data();
                driverName = driverData.status === 'deleted' ? 'Deleted Account' : driverData.name;
              }
            } catch (error) {
              console.error('Error fetching driver:', error);
            }
          }
          
          return {
            id: orderDoc.id,
            ...orderData,
            driverName
          };
        })
      );

      setOrders(ordersData);
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const filterOrders = () => {
    let filtered = [...orders];

    if (searchTerm) {
      filtered = filtered.filter(order => {
        const search = searchTerm.toLowerCase();
        const orderNum = order.subNumber 
          ? `#${order.orderNumber}-${order.subNumber}`
          : `#${order.orderNumber}`;
        
        return (
          orderNum.toLowerCase().includes(search) ||
          order.senderName?.toLowerCase().includes(search) ||
          order.receiverName?.toLowerCase().includes(search) ||
          order.driverName?.toLowerCase().includes(search) ||
          order.senderNumber?.includes(search) ||
          order.receiverNumber?.includes(search)
        );
      });
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(order => order.status === statusFilter);
    }

    if (dateFilter !== 'all') {
      const now = new Date();
      filtered = filtered.filter(order => {
        const orderDate = order.createdAt?.toDate();
        if (!orderDate) return false;

        switch (dateFilter) {
          case 'today':
            return orderDate.toDateString() === now.toDateString();
          case 'week':
            const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            return orderDate >= weekAgo;
          case 'month':
            const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
            return orderDate >= monthAgo;
          default:
            return true;
        }
      });
    }

    if (exactDate) {
      filtered = filtered.filter(order => {
        const orderDate = order.createdAt?.toDate();
        if (!orderDate) return false;
        
        const selectedDate = new Date(exactDate);
        return orderDate.toDateString() === selectedDate.toDateString();
      });
    }

    setFilteredOrders(filtered);
  };

  const totalPages = Math.ceil(filteredOrders.length / ordersPerPage);
  const startIndex = (currentPage - 1) * ordersPerPage;
  const endIndex = startIndex + ordersPerPage;
  const currentOrders = filteredOrders.slice(startIndex, endIndex);

  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const getOrderNumber = (order) => {
    const orderNum = String(order.orderNumber).padStart(8, '0');
    const subNum = order.subNumber ? String(order.subNumber).padStart(2, '0') : null;
    return subNum ? `#${orderNum}-${subNum}` : `#${orderNum}`;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      waiting: { 
        variant: 'warning', 
        label: 'Waiting',
        color: 'bg-yellow-100 text-yellow-800 border-yellow-300'
      },
      accepted: { 
        variant: 'info', 
        label: 'Accepted',
        color: 'bg-blue-100 text-blue-800 border-blue-300'
      },
      in_progress: { 
        variant: 'info', 
        label: 'In Progress',
        color: 'bg-purple-100 text-purple-800 border-purple-300'
      },
      returned_pending: { 
        variant: 'warning', 
        label: 'Returned Pending',
        color: 'bg-orange-100 text-orange-800 border-orange-300'
      },
      returned: { 
        variant: 'info', 
        label: 'Returned',
        color: 'bg-cyan-100 text-cyan-800 border-cyan-300'
      },
      completed: { 
        variant: 'success', 
        label: 'Completed',
        color: 'bg-green-100 text-green-800 border-green-300'
      },
      canceled: { 
        variant: 'danger', 
        label: 'Canceled',
        color: 'bg-red-100 text-red-800 border-red-300'
      },
      pending: { 
        variant: 'warning', 
        label: 'Pending',
        color: 'bg-yellow-100 text-yellow-800 border-yellow-300'
      },
      picked_up: { 
        variant: 'info', 
        label: 'Picked Up',
        color: 'bg-blue-100 text-blue-800 border-blue-300'
      },
      in_transit: { 
        variant: 'info', 
        label: 'In Transit',
        color: 'bg-purple-100 text-purple-800 border-purple-300'
      },
      delivered: { 
        variant: 'success', 
        label: 'Delivered',
        color: 'bg-green-100 text-green-800 border-green-300'
      },
      rejected: { 
        variant: 'danger', 
        label: 'Rejected',
        color: 'bg-red-100 text-red-800 border-red-300'
      }
    };

    const config = statusConfig[status] || { 
      variant: 'default', 
      label: status,
      color: 'bg-gray-100 text-gray-800 border-gray-300'
    };
    
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return `${amount?.toFixed(2) || '0.00'} JOD`;
  };

  const isCancelableStatus = (status) =>
    status !== 'canceled' && status !== 'completed' && status !== 'delivered';

  const openCancelModal = (order) => {
    setCancelOrder(order);
    // Default refund ON when there's an active driver + deduction.
    const hasDeduction =
      !!order.driverId && Number(order.deductionAmount || 0) > 0;
    setCancelRefundDriver(hasDeduction);
    setCancelReason('');
  };

  const closeCancelModal = () => {
    if (cancelSubmitting) return;
    setCancelOrder(null);
  };

  const submitCancelOrder = async () => {
    if (!cancelOrder) return;
    try {
      setCancelSubmitting(true);
      const fns = getFunctions();
      const callable = httpsCallable(fns, 'adminCancelOrder');
      const res = await callable({
        orderId: cancelOrder.id,
        refundDriver: cancelRefundDriver,
        reason: cancelReason.trim() || null,
      });
      const refunded = res.data?.refunded;
      const amount = res.data?.refundAmount || 0;
      logAdminActivity({
        actor: currentAdmin,
        action: 'cancel_order',
        targetType: 'order',
        targetId: cancelOrder.id,
        targetLabel: `Order #${String(cancelOrder.orderNumber || '').padStart(6, '0')}`,
      });
      toast.success(
        refunded
          ? `Order canceled. Refunded ${amount} JOD to driver.`
          : 'Order canceled.',
      );
      setCancelOrder(null);
      setShowDetailsModal(false);
      await fetchOrders();
    } catch (err) {
      console.error('Cancel order error:', err);
      toast.error(`Failed: ${err.message}`);
    } finally {
      setCancelSubmitting(false);
    }
  };

  const viewOrderDetails = async (order) => {
    setSelectedOrder(order);
    setShowDetailsModal(true);
    
    if (order.customerId) {
      setLoadingCustomer(true);
      try {
        const customerDoc = await getDoc(doc(db, 'users', order.customerId));
        if (customerDoc.exists()) {
          setCustomerInfo(customerDoc.data());
        } else {
          setCustomerInfo(null);
        }
      } catch (error) {
        console.error('Error fetching customer info:', error);
        setCustomerInfo(null);
      } finally {
        setLoadingCustomer(false);
      }
    } else {
      setCustomerInfo(null);
    }

    if (order.driverId) {
      setLoadingDriver(true);
      try {
        const driverDoc = await getDoc(doc(db, 'users', order.driverId));
        if (driverDoc.exists()) {
          setDriverInfo(driverDoc.data());
        } else {
          setDriverInfo(null);
        }
      } catch (error) {
        console.error('Error fetching driver info:', error);
        setDriverInfo(null);
      } finally {
        setLoadingDriver(false);
      }
    } else {
      setDriverInfo(null);
    }
  };

  const viewUserInfo = async (userId, userType) => {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        
        if (userData.status === 'deleted') {
          toast.error('This account has been deleted');
          return;
        }
        
        setSelectedUser({
          ...userData,
          id: userDoc.id,
          type: userType
        });
        setShowUserModal(true);
      } else {
        toast.error('User not found');
      }
    } catch (error) {
      console.error('Error fetching user:', error);
      toast.error('Failed to load user information');
    }
  };

  const openImage = (imageUrl, title) => {
    setSelectedImage({ url: imageUrl, title });
    setShowImageModal(true);
  };

  const getStatusCounts = () => {
    return {
      all: orders.length,
      waiting: orders.filter(o => o.status === 'waiting' || o.status === 'pending').length,
      accepted: orders.filter(o => o.status === 'accepted').length,
      in_progress: orders.filter(o => o.status === 'in_progress' || o.status === 'picked_up' || o.status === 'in_transit').length,
      returned_pending: orders.filter(o => o.status === 'returned_pending').length,
      returned: orders.filter(o => o.status === 'returned').length,
      completed: orders.filter(o => o.status === 'completed' || o.status === 'delivered').length,
      canceled: orders.filter(o => o.status === 'canceled').length,
      rejected: orders.filter(o => o.status === 'rejected').length
    };
  };

  const exportToCSV = () => {
    const headers = [
      'Order Number', 
      'Status', 
      'Sender', 
      'Sender Phone',
      'Receiver', 
      'Receiver Phone',
      'Driver', 
      'From Area',
      'From City',
      'To Area',
      'To City',
      'Item Price',
      'Delivery Price',
      'Total Price', 
      'Created At'
    ];
    const rows = filteredOrders.map(order => [
      getOrderNumber(order),
      order.status,
      order.senderName,
      order.senderNumber,
      order.receiverName || 'N/A',
      order.receiverNumber,
      order.driverName || 'N/A',
      order.startPlace?.area || 'N/A',
      order.startPlace?.city || 'N/A',
      order.endPlace?.area || 'N/A',
      order.endPlace?.city || 'N/A',
      order.itemPrice || 0,
      order.deliveryPrice || 0,
      order.totalPrice || 0,
      formatDate(order.createdAt)
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `orders-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const statusCounts = getStatusCounts();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 font-arabic">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Orders Management</h1>
          <p className="text-gray-600 mt-1">View and manage all delivery orders</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={fetchOrders}
            disabled={loading}
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button variant="outline" onClick={exportToCSV}>
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-4">
        <Card>
          <CardBody>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total</p>
                <p className="text-2xl font-bold text-gray-900">{statusCounts.all}</p>
              </div>
              <Package className="w-10 h-10 text-primary-600 opacity-50" />
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Waiting</p>
                <p className="text-2xl font-bold text-yellow-600">{statusCounts.waiting}</p>
              </div>
              <Clock className="w-10 h-10 text-yellow-600 opacity-50" />
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Accepted</p>
                <p className="text-2xl font-bold text-blue-600">{statusCounts.accepted}</p>
              </div>
              <CheckCircle className="w-10 h-10 text-blue-600 opacity-50" />
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">In Progress</p>
                <p className="text-2xl font-bold text-purple-600">{statusCounts.in_progress}</p>
              </div>
              <Navigation className="w-10 h-10 text-purple-600 opacity-50" />
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Completed</p>
                <p className="text-2xl font-bold text-green-600">{statusCounts.completed}</p>
              </div>
              <Package className="w-10 h-10 text-green-600 opacity-50" />
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Returned</p>
                <p className="text-2xl font-bold text-cyan-600">{statusCounts.returned + statusCounts.returned_pending}</p>
              </div>
              <RefreshCw className="w-10 h-10 text-cyan-600 opacity-50" />
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Canceled</p>
                <p className="text-2xl font-bold text-red-600">{statusCounts.canceled}</p>
              </div>
              <X className="w-10 h-10 text-red-600 opacity-50" />
            </div>
          </CardBody>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardBody>
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                type="text"
                placeholder="Search by order number, customer, driver, or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Filter className="w-4 h-4 inline mr-1" />
                Filter by Status
              </label>
              <div className="flex flex-wrap gap-2">
                <Button variant={statusFilter === 'all' ? 'primary' : 'outline'} size="sm" onClick={() => setStatusFilter('all')}>
                  All ({statusCounts.all})
                </Button>
                <Button variant={statusFilter === 'waiting' ? 'primary' : 'outline'} size="sm" onClick={() => setStatusFilter('waiting')}>
                  Waiting ({statusCounts.waiting})
                </Button>
                <Button variant={statusFilter === 'accepted' ? 'primary' : 'outline'} size="sm" onClick={() => setStatusFilter('accepted')}>
                  Accepted ({statusCounts.accepted})
                </Button>
                <Button variant={statusFilter === 'in_progress' ? 'primary' : 'outline'} size="sm" onClick={() => setStatusFilter('in_progress')}>
                  In Progress ({statusCounts.in_progress})
                </Button>
                <Button variant={statusFilter === 'completed' ? 'primary' : 'outline'} size="sm" onClick={() => setStatusFilter('completed')}>
                  Completed ({statusCounts.completed})
                </Button>
                <Button variant={statusFilter === 'canceled' ? 'primary' : 'outline'} size="sm" onClick={() => setStatusFilter('canceled')}>
                  Canceled ({statusCounts.canceled})
                </Button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Calendar className="w-4 h-4 inline mr-1" />
                Filter by Date
              </label>
              <div className="flex flex-wrap gap-2 items-center">
                <Button variant={dateFilter === 'all' ? 'primary' : 'outline'} size="sm" onClick={() => { setDateFilter('all'); setExactDate(''); }}>
                  All Time
                </Button>
                <Button variant={dateFilter === 'today' ? 'primary' : 'outline'} size="sm" onClick={() => { setDateFilter('today'); setExactDate(''); }}>
                  Today
                </Button>
                <Button variant={dateFilter === 'week' ? 'primary' : 'outline'} size="sm" onClick={() => { setDateFilter('week'); setExactDate(''); }}>
                  Last 7 Days
                </Button>
                <Button variant={dateFilter === 'month' ? 'primary' : 'outline'} size="sm" onClick={() => { setDateFilter('month'); setExactDate(''); }}>
                  Last 30 Days
                </Button>
                <div className="flex items-center gap-2 ml-4">
                  <span className="text-sm text-gray-600">Or select exact date:</span>
                  <Input type="date" value={exactDate} onChange={(e) => { setExactDate(e.target.value); setDateFilter('all'); }} className="w-auto" />
                  {exactDate && <Button variant="outline" size="sm" onClick={() => setExactDate('')}>Clear</Button>}
                </div>
              </div>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Orders Table */}
      <Card>
        <CardHeader>
          <h2 className="text-xl font-semibold">Orders List ({filteredOrders.length})</h2>
        </CardHeader>
        <CardBody>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order #</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sender</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Receiver</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Driver</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Route</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Price</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created At</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {currentOrders.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="px-6 py-12 text-center text-gray-500">
                      <Package className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                      <p className="font-medium">No orders found</p>
                      <p className="text-sm mt-1">Try adjusting your filters</p>
                    </td>
                  </tr>
                ) : (
                  currentOrders.map((order) => (
                    <tr key={order.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <Package className="w-5 h-5 text-primary-600 mr-2" />
                          <span className="font-semibold text-primary-600">{getOrderNumber(order)}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">{getStatusBadge(order.status)}</td>
                      <td className="px-6 py-4">
                        <div>
                          <p className="font-medium text-gray-900">{order.senderName}</p>
                          <p className="text-sm text-gray-500">{order.senderNumber}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <p className="font-medium text-gray-900">{order.receiverName || 'N/A'}</p>
                          <p className="text-sm text-gray-500">{order.receiverNumber}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {order.driverId ? (
                          <span className={`font-medium ${order.driverName === 'Deleted Account' ? 'text-red-600' : 'text-gray-900'}`}>
                            {order.driverName}
                          </span>
                        ) : (
                          <span className="text-gray-400">Not assigned</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm">
                          <div className="flex items-center text-gray-900">
                            <MapPin className="w-4 h-4 text-green-600 mr-1" />
                            {order.startPlace?.area} - {order.startPlace?.city || 'N/A'}
                          </div>
                          <div className="flex items-center text-gray-500 mt-1">
                            <MapPin className="w-4 h-4 text-red-600 mr-1" />
                            {order.endPlace?.area} - {order.endPlace?.city || 'N/A'}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="font-semibold text-gray-900">{formatCurrency(order.totalPrice)}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(order.createdAt)}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Button variant="outline" size="sm" onClick={() => viewOrderDetails(order)}>
                          <Eye className="w-4 h-4 mr-1" />
                          Details
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 mt-4">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
                  <span className="font-medium">{Math.min(endIndex, filteredOrders.length)}</span> of{' '}
                  <span className="font-medium">{filteredOrders.length}</span> orders
                </div>
                
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="p-2 rounded-lg border border-gray-300 hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  
                  <div className="flex items-center gap-1">
                    {[...Array(totalPages)].map((_, i) => {
                      const page = i + 1;
                      if (
                        page === 1 ||
                        page === totalPages ||
                        (page >= currentPage - 1 && page <= currentPage + 1)
                      ) {
                        return (
                          <button
                            key={page}
                            onClick={() => goToPage(page)}
                            className={`min-w-[40px] px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                              currentPage === page
                                ? 'bg-primary-600 text-white'
                                : 'border border-gray-300 hover:bg-white text-gray-700'
                            }`}
                          >
                            {page}
                          </button>
                        );
                      } else if (page === currentPage - 2 || page === currentPage + 2) {
                        return (
                          <span key={page} className="px-2 text-gray-400">
                            ...
                          </span>
                        );
                      }
                      return null;
                    })}
                  </div>
                  
                  <button
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="p-2 rounded-lg border border-gray-300 hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Order Details Modal */}
      {showDetailsModal && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 font-arabic">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 flex items-center">
                  <Package className="w-6 h-6 mr-2 text-primary-600" />
                  Order {getOrderNumber(selectedOrder)}
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  Created: {formatDate(selectedOrder.createdAt)}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {canCancel && isCancelableStatus(selectedOrder.status) && (
                  <button
                    onClick={() => openCancelModal(selectedOrder)}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors"
                  >
                    <X className="w-4 h-4" />
                    Cancel Order
                  </button>
                )}
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Status */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Current Status</p>
                  <div>{getStatusBadge(selectedOrder.status)}</div>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-600">Order ID</p>
                  <p className="text-sm font-mono text-gray-900">{selectedOrder.id}</p>
                </div>
              </div>

              {/* Customer Information */}
              <div className="border border-gray-200 rounded-lg p-4">
                <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <User className="w-5 h-5 mr-2 text-primary-600" />
                  Customer Information
                </h4>
                {loadingCustomer ? (
                  <div className="flex justify-center py-4">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                  </div>
                ) : customerInfo ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">Name</p>
                      <p className="text-sm font-medium text-gray-900">{customerInfo.name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Email</p>
                      <p className="text-sm font-medium text-gray-900">{customerInfo.email || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Phone</p>
                      <p className="text-sm font-medium text-gray-900">{customerInfo.phoneNumber}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Customer ID</p>
                      <p className="text-sm font-mono text-gray-900">{selectedOrder.customerId}</p>
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">Customer information not available</p>
                )}
              </div>

              {/* Sender Information */}
              <div className="border border-gray-200 rounded-lg p-4">
                <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <MapPin className="w-5 h-5 mr-2 text-green-600" />
                  Sender Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Name</p>
                    <p className="text-sm font-medium text-gray-900">{selectedOrder.senderName}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Phone</p>
                    <p className="text-sm font-medium text-gray-900">{selectedOrder.senderNumber}</p>
                  </div>
                  <div className="md:col-span-2">
                    <p className="text-sm text-gray-600">Pickup Location</p>
                    <p className="text-sm font-medium text-gray-900">
                      {selectedOrder.startPlace?.area}, {selectedOrder.startPlace?.city || 'N/A'}
                    </p>
                    {selectedOrder.startPlace?.address && (
                      <p className="text-sm text-gray-500 mt-1">{selectedOrder.startPlace.address}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Receiver Information */}
              <div className="border border-gray-200 rounded-lg p-4">
                <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <MapPin className="w-5 h-5 mr-2 text-red-600" />
                  Receiver Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Name</p>
                    <p className="text-sm font-medium text-gray-900">{selectedOrder.receiverName || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Phone</p>
                    <p className="text-sm font-medium text-gray-900">{selectedOrder.receiverNumber}</p>
                  </div>
                  <div className="md:col-span-2">
                    <p className="text-sm text-gray-600">Delivery Location</p>
                    <p className="text-sm font-medium text-gray-900">
                      {selectedOrder.endPlace?.area}, {selectedOrder.endPlace?.city || 'N/A'}
                    </p>
                    {selectedOrder.endPlace?.address && (
                      <p className="text-sm text-gray-500 mt-1">{selectedOrder.endPlace.address}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Driver Information */}
              {selectedOrder.driverId && (
                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <Truck className="w-5 h-5 mr-2 text-primary-600" />
                    Driver Information
                  </h4>
                  {loadingDriver ? (
                    <div className="flex justify-center py-4">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                    </div>
                  ) : driverInfo ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-600">Name</p>
                        <p className="text-sm font-medium text-gray-900">{driverInfo.name}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Phone</p>
                        <p className="text-sm font-medium text-gray-900">{driverInfo.phoneNumber}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Email</p>
                        <p className="text-sm font-medium text-gray-900">{driverInfo.email || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Driver ID</p>
                        <p className="text-sm font-mono text-gray-900">{selectedOrder.driverId}</p>
                      </div>
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm">Driver information not available</p>
                  )}
                </div>
              )}

              {/* Package Details */}
              <div className="border border-gray-200 rounded-lg p-4">
                <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Package className="w-5 h-5 mr-2 text-primary-600" />
                  Package Details
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {selectedOrder.packageType && (
                    <div>
                      <p className="text-sm text-gray-600">Package Type</p>
                      <p className="text-sm font-medium text-gray-900">{selectedOrder.packageType}</p>
                    </div>
                  )}
                  {selectedOrder.packageSize && (
                    <div>
                      <p className="text-sm text-gray-600">Package Size</p>
                      <p className="text-sm font-medium text-gray-900">{selectedOrder.packageSize}</p>
                    </div>
                  )}
                  {selectedOrder.weight && (
                    <div>
                      <p className="text-sm text-gray-600">Weight</p>
                      <p className="text-sm font-medium text-gray-900">{selectedOrder.weight} kg</p>
                    </div>
                  )}
                  {selectedOrder.description && (
                    <div className="md:col-span-2">
                      <p className="text-sm text-gray-600">Description</p>
                      <p className="text-sm font-medium text-gray-900">{selectedOrder.description}</p>
                    </div>
                  )}
                  {selectedOrder.notes && (
                    <div className="md:col-span-2">
                      <p className="text-sm text-gray-600">Notes</p>
                      <p className="text-sm font-medium text-gray-900">{selectedOrder.notes}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Package Image */}
              {selectedOrder.packageImage && (
                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <ImageIcon className="w-5 h-5 mr-2 text-primary-600" />
                    Package Image
                  </h4>
                  <div className="relative">
                    <img
                      src={selectedOrder.packageImage}
                      alt="Package"
                      className="w-full max-w-md h-64 object-cover rounded-lg cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={() => openImage(selectedOrder.packageImage, 'Package Image')}
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openImage(selectedOrder.packageImage, 'Package Image')}
                      className="mt-2"
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      View Full Size
                    </Button>
                  </div>
                </div>
              )}

              {/* Payment Information */}
              <div className="border border-gray-200 rounded-lg p-4">
                <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <CreditCard className="w-5 h-5 mr-2 text-primary-600" />
                  Payment Information
                </h4>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Item Price</span>
                    <span className="text-sm font-medium text-gray-900">{formatCurrency(selectedOrder.itemPrice)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Delivery Price</span>
                    <span className="text-sm font-medium text-gray-900">{formatCurrency(selectedOrder.deliveryPrice)}</span>
                  </div>
                  <div className="flex justify-between items-center pt-3 border-t border-gray-200">
                    <span className="text-base font-semibold text-gray-900">Total Price</span>
                    <span className="text-lg font-bold text-primary-600">{formatCurrency(selectedOrder.totalPrice)}</span>
                  </div>
                  {selectedOrder.paymentMethod && (
                    <div className="flex justify-between items-center pt-2">
                      <span className="text-sm text-gray-600">Payment Method</span>
                      <span className="text-sm font-medium text-gray-900">{selectedOrder.paymentMethod}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Timestamps */}
              <div className="border border-gray-200 rounded-lg p-4">
                <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Clock className="w-5 h-5 mr-2 text-primary-600" />
                  Timeline
                </h4>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Created At</span>
                    <span className="text-sm font-medium text-gray-900">{formatDate(selectedOrder.createdAt)}</span>
                  </div>
                  {selectedOrder.acceptedAt && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Accepted At</span>
                      <span className="text-sm font-medium text-gray-900">{formatDate(selectedOrder.acceptedAt)}</span>
                    </div>
                  )}
                  {selectedOrder.inProgressAt && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">In Processing At</span>
                      <span className="text-sm font-medium text-gray-900">{formatDate(selectedOrder.inProgressAt)}</span>
                    </div>
                  )}
                  {selectedOrder.pickedUpAt && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Picked Up At</span>
                      <span className="text-sm font-medium text-gray-900">{formatDate(selectedOrder.pickedUpAt)}</span>
                    </div>
                  )}
                  {selectedOrder.deliveredAt && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Delivered At</span>
                      <span className="text-sm font-medium text-gray-900">{formatDate(selectedOrder.deliveredAt)}</span>
                    </div>
                  )}
                  {selectedOrder.completedAt && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Completed At</span>
                      <span className="text-sm font-medium text-gray-900">{formatDate(selectedOrder.completedAt)}</span>
                    </div>
                  )}
                  {selectedOrder.returnedAt && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Returned At</span>
                      <span className="text-sm font-medium text-orange-600">{formatDate(selectedOrder.returnedAt)}</span>
                    </div>
                  )}
                  {selectedOrder.canceledAt && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Canceled At</span>
                      <span className="text-sm font-medium text-red-600">{formatDate(selectedOrder.canceledAt)}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Cancel Information */}
              {selectedOrder.status === 'canceled' && selectedOrder.canceledBy && (() => {
                const cancelBy = selectedOrder.canceledBy;
                let role;
                let subtitle;
                if (cancelBy === 'system') {
                  role = 'System (auto-canceled)';
                  subtitle = 'No driver accepted in time';
                } else if (selectedOrder.driverId && cancelBy === selectedOrder.driverId) {
                  role = 'Driver';
                } else if (selectedOrder.customerId && cancelBy === selectedOrder.customerId) {
                  role = 'Customer';
                } else {
                  role = 'Unknown';
                }
                return (
                  <div className="border border-red-200 bg-red-50 rounded-lg p-4">
                    <h4 className="text-lg font-semibold text-red-900 mb-3 flex items-center">
                      <MessageSquare className="w-5 h-5 mr-2" />
                      Cancellation Information
                    </h4>
                    <div className="space-y-2">
                      <div>
                        <p className="text-sm text-red-700 font-medium">Canceled by:</p>
                        <p className="text-sm font-semibold text-red-900">{role}</p>
                        {subtitle && (
                          <p className="text-xs text-red-700">{subtitle}</p>
                        )}
                      </div>
                      {selectedOrder.cancelReason && (
                        <div>
                          <p className="text-sm text-red-700 font-medium">Reason:</p>
                          <p className="text-sm text-red-900">{selectedOrder.cancelReason}</p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Internal staff notes on this order */}
              <div className="border border-gray-200 rounded-lg p-4">
                <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <StickyNote className="w-5 h-5 mr-2 text-primary-600" />
                  Order Notes
                </h4>
                <AdminNotes
                  parentSegments={['orders', selectedOrder.id]}
                  targetType="order"
                  targetLabel={`Order ${getOrderNumber(selectedOrder)}`}
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex items-center justify-end gap-3">
              <Button variant="outline" onClick={() => setShowDetailsModal(false)}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* User Info Modal */}
      {showUserModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 font-arabic">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-gray-900 flex items-center">
                  <User className="w-6 h-6 mr-2 text-primary-600" />
                  {selectedUser.type === 'driver' ? 'Driver' : 'Customer'} Information
                </h3>
                <p className="text-sm text-gray-500 mt-1">{selectedUser.name}</p>
              </div>
              <button
                onClick={() => setShowUserModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Profile Photo */}
              {selectedUser.profilePhoto && (
                <div className="flex justify-center">
                  <img
                    src={selectedUser.profilePhoto}
                    alt={selectedUser.name}
                    className="w-24 h-24 rounded-full object-cover border-4 border-gray-200 cursor-pointer hover:opacity-80 transition-opacity"
                    onClick={() => openImage(selectedUser.profilePhoto, selectedUser.name)}
                  />
                </div>
              )}

              {/* User Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Name</p>
                  <p className="text-sm font-medium text-gray-900">{selectedUser.name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Email</p>
                  <p className="text-sm font-medium text-gray-900">{selectedUser.email || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Phone</p>
                  <p className="text-sm font-medium text-gray-900">{selectedUser.phoneNumber}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">User ID</p>
                  <p className="text-sm font-mono text-gray-900">{selectedUser.id}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Status</p>
                  <div className="mt-1">
                    {selectedUser.status === 'active' ? (
                      <Badge variant="success">Active</Badge>
                    ) : (
                      <Badge variant="default">{selectedUser.status}</Badge>
                    )}
                  </div>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Account Type</p>
                  <p className="text-sm font-medium text-gray-900 capitalize">{selectedUser.type}</p>
                </div>
                {selectedUser.wallet_balance !== undefined && (
                  <div>
                    <p className="text-sm text-gray-600">Wallet Balance</p>
                    <p className="text-sm font-medium text-gray-900">{formatCurrency(selectedUser.wallet_balance)}</p>
                  </div>
                )}
                {selectedUser.total_orders_delivered !== undefined && (
                  <div>
                    <p className="text-sm text-gray-600">Orders Delivered</p>
                    <p className="text-sm font-medium text-gray-900">{selectedUser.total_orders_delivered || 0}</p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-gray-600">Joined</p>
                  <p className="text-sm font-medium text-gray-900">{formatDate(selectedUser.createdAt)}</p>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex items-center justify-end">
              <Button variant="outline" onClick={() => setShowUserModal(false)}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Image Modal */}
      {showImageModal && selectedImage && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4"
          onClick={() => setShowImageModal(false)}
        >
          <div className="relative max-w-7xl max-h-full">
            <button
              onClick={() => setShowImageModal(false)}
              className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors"
            >
              <X className="w-8 h-8" />
            </button>
            
            {selectedImage.title && (
              <div className="absolute -top-12 left-0 text-white text-lg font-medium">
                {selectedImage.title}
              </div>
            )}
            
            <img
              src={selectedImage.url}
              alt={selectedImage.title || 'Image'}
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}

      {cancelOrder && (
        <div
          className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4"
          onClick={closeCancelModal}
        >
          <div
            className="bg-white rounded-2xl shadow-xl w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="text-lg font-semibold flex items-center gap-2 text-red-600">
                <X className="w-5 h-5" /> Cancel Order
              </h3>
              <button
                onClick={closeCancelModal}
                disabled={cancelSubmitting}
                className="p-1 rounded hover:bg-slate-100"
              >
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-slate-50 rounded-lg p-3 text-sm">
                <p className="text-slate-700">
                  Order: <span className="font-mono font-semibold">{getOrderNumber(cancelOrder)}</span>
                </p>
                <p className="text-slate-700 mt-1">
                  Status: <span className="font-semibold">{cancelOrder.status}</span>
                </p>
                {cancelOrder.driverId ? (
                  <p className="text-slate-700 mt-1">
                    Driver deduction:{' '}
                    <span className="font-semibold">
                      {Number(cancelOrder.deductionAmount || 0).toFixed(2)} JOD
                    </span>
                  </p>
                ) : (
                  <p className="text-slate-500 mt-1 italic">No driver assigned</p>
                )}
              </div>

              <label
                className={`flex items-start gap-2 ${
                  !cancelOrder.driverId || !(cancelOrder.deductionAmount > 0)
                    ? 'opacity-50 cursor-not-allowed'
                    : 'cursor-pointer'
                }`}
              >
                <input
                  type="checkbox"
                  checked={cancelRefundDriver}
                  onChange={(e) => setCancelRefundDriver(e.target.checked)}
                  disabled={
                    cancelSubmitting ||
                    !cancelOrder.driverId ||
                    !(cancelOrder.deductionAmount > 0)
                  }
                  className="mt-1 w-4 h-4"
                />
                <div className="text-sm">
                  <p className="font-medium text-slate-900">
                    Refund driver's deduction
                  </p>
                  <p className="text-slate-500 text-xs">
                    Adds a refund transaction of{' '}
                    {Number(cancelOrder.deductionAmount || 0).toFixed(2)} JOD to the
                    driver's wallet.
                  </p>
                </div>
              </label>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Reason (optional)
                </label>
                <textarea
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  disabled={cancelSubmitting}
                  rows={2}
                  placeholder="e.g. Customer requested by phone"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-500 text-sm"
                />
              </div>
            </div>

            <div className="px-6 py-4 border-t flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={closeCancelModal}
                disabled={cancelSubmitting}
              >
                Back
              </Button>
              <button
                onClick={submitCancelOrder}
                disabled={cancelSubmitting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 text-sm font-medium"
              >
                {cancelSubmitting ? 'Canceling...' : 'Confirm Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Orders;